/*
 * pdp.js: shared behaviour of the pdp-* sections.
 *
 *   window.PDP        headerOffset(), scrollToEl(el, extraPx), onSection(target, fn), debounce(fn, ms),
 *                     mqDesktop (matchMedia '(min-width:750px)'), reducedMotion (boolean)
 *   custom elements   <pdp-carousel> <pdp-marquee> <pdp-tabs> <pdp-accordion> <pdp-video> <pdp-hotspot> <pdp-sticky-bar>
 *   reveal            [data-pdp-reveal] gets .is-revealed once it scrolls into view
 *   on <html>         class pdp-js, --pdp-header-h, --pdp-sticky-h
 *
 * Vanilla, no dependencies, no build step. Every element sets itself up in connectedCallback and cleans up in
 * disconnectedCallback, so a section re-rendered by the theme editor simply works. The <script> tag is printed by
 * every pdp-* section, and a repeated classic script runs once per tag: hence the guard on the next line.
 */
(function () {
  'use strict';
  if (window.PDP) return;

  var win = window;
  var doc = document;
  var html = doc.documentElement;

  html.classList.add('pdp-js');

  /* ------------------------------------------------------------------ utilities */

  function noop() {}

  function debounce(fn, ms) {
    var t;
    return function () {
      var self = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, ms == null ? 200 : ms);
    };
  }

  function media(query) {
    try {
      if (win.matchMedia) return win.matchMedia(query);
    } catch (e) { /* fall through */ }
    return { matches: false, media: query, addEventListener: noop, removeEventListener: noop, addListener: noop, removeListener: noop };
  }

  function onMedia(mq, fn) {
    if (mq.addEventListener) mq.addEventListener('change', fn);
    else if (mq.addListener) mq.addListener(fn);
  }

  function offMedia(mq, fn) {
    if (mq.removeEventListener) mq.removeEventListener('change', fn);
    else if (mq.removeListener) mq.removeListener(fn);
  }

  function clamp(n, min, max) {
    return Math.min(Math.max(n, min), max);
  }

  function num(value, fallback) {
    var n = parseFloat(value);
    return isFinite(n) ? n : (fallback || 0);
  }

  function fire(el, name, detail, bubbles) {
    try {
      el.dispatchEvent(new CustomEvent(name, { bubbles: bubbles !== false, detail: detail || {} }));
    } catch (e) { /* a listener threw: not ours to die for */ }
  }

  function inDesignMode() {
    return !!(win.Shopify && win.Shopify.designMode);
  }

  function setInert(el, on) {
    if (!el) return;
    if (on) el.setAttribute('inert', '');
    else el.removeAttribute('inert');
  }

  /* Runs fn once the element's children can be relied on (they always can when this file is deferred). */
  function whenParsed(fn) {
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  }

  var mqDesktop = media('(min-width:750px)');
  var mqReduce = media('(prefers-reduced-motion: reduce)');
  var mqHover = media('(hover: hover) and (pointer: fine)');

  /*
   * Height of .wr-header when it EFFECTIVELY sticks, else 0. A sticky box only travels inside its parent, so a
   * sticky header whose wrapper is exactly its own height never sticks (Wildroot today: returns 0). No scroll probing.
   */
  function headerOffset() {
    try {
      var header = doc.querySelector('.wr-header');
      if (!header) return 0;
      for (var el = header; el && el !== doc.body && el.parentElement; el = el.parentElement) {
        var pos = win.getComputedStyle(el).position;
        var roomy = el.parentElement.offsetHeight > el.offsetHeight + 1;
        if (pos === 'fixed' || (pos === 'sticky' && roomy)) return header.offsetHeight;
        if (roomy) return 0;
      }
    } catch (e) { /* fall through */ }
    return 0;
  }

  function scrollToEl(target, extraPx) {
    var el = typeof target === 'string' ? doc.querySelector(target) : target;
    if (!el || !el.getBoundingClientRect) return;
    var extra = extraPx == null ? 14 : extraPx;
    var top = el.getBoundingClientRect().top + (win.pageYOffset || html.scrollTop || 0) - (headerOffset() + extra);
    top = Math.max(0, Math.round(top));
    try {
      win.scrollTo({ top: top, behavior: PDP.reducedMotion ? 'auto' : 'smooth' });
    } catch (e) {
      win.scrollTo(0, top);
    }
  }

  /*
   * onSection(target, fn): run fn(el) now, and again with the fresh element each time the theme editor re-renders
   * the Shopify section that holds it. target is an element or a selector. If fn returns a function, it is called
   * when that section is unloaded (clean-up). Returns a function that stops listening.
   */
  function onSection(target, fn) {
    if (typeof fn !== 'function' || !target) return noop;
    var selector = typeof target === 'string' ? target : null;
    var cleanups = [];

    function run(el) {
      if (!el) return;
      var off;
      try { off = fn(el); } catch (e) { if (win.console) win.console.error('[pdp] onSection', e); }
      cleanups.push({ el: el, off: typeof off === 'function' ? off : null });
    }

    function wrapperOf(el) {
      return el.closest ? el.closest('.shopify-section') : null;
    }

    if (selector) {
      Array.prototype.forEach.call(doc.querySelectorAll(selector), run);
    } else {
      run(target);
      /* remember how to find the same element again after a re-render */
      if (target.id) selector = '#' + (win.CSS && win.CSS.escape ? win.CSS.escape(target.id) : target.id);
      else if (target.classList && target.classList.length) selector = target.tagName.toLowerCase() + '.' + Array.prototype.join.call(target.classList, '.');
    }

    var wrapper = typeof target === 'string' ? null : wrapperOf(target);
    var wrapperId = wrapper ? wrapper.id : null;

    function onLoad(event) {
      var section = event.target;
      if (!section || !section.querySelectorAll) return;
      if (wrapperId && section.id !== wrapperId) return;
      if (!selector) { run(section); return; }
      var found = section.matches && section.matches(selector) ? [section] : section.querySelectorAll(selector);
      Array.prototype.forEach.call(found, run);
    }

    function onUnload(event) {
      var section = event.target;
      cleanups = cleanups.filter(function (entry) {
        if (section && section.contains && section.contains(entry.el)) {
          if (entry.off) { try { entry.off(); } catch (e) { /* ignore */ } }
          return false;
        }
        return true;
      });
    }

    doc.addEventListener('shopify:section:load', onLoad);
    doc.addEventListener('shopify:section:unload', onUnload);
    return function () {
      doc.removeEventListener('shopify:section:load', onLoad);
      doc.removeEventListener('shopify:section:unload', onUnload);
    };
  }

  var PDP = {
    headerOffset: headerOffset,
    scrollToEl: scrollToEl,
    onSection: onSection,
    debounce: debounce,
    mqDesktop: mqDesktop,
    reducedMotion: !!mqReduce.matches
  };
  win.PDP = PDP;

  onMedia(mqReduce, function (e) { PDP.reducedMotion = !!e.matches; });

  function writeHeaderHeight() {
    html.style.setProperty('--pdp-header-h', headerOffset() + 'px');
  }

  writeHeaderHeight();
  win.addEventListener('load', writeHeaderHeight);
  win.addEventListener('resize', debounce(writeHeaderHeight, 150), { passive: true });
  doc.addEventListener('shopify:section:load', debounce(writeHeaderHeight, 50));

  /* ------------------------------------------------------------------ reveal */

  var revealObserver = null;

  function reveal(el) {
    el.classList.add('is-revealed');
    if (revealObserver) revealObserver.unobserve(el);
  }

  function watchReveal(scope) {
    var roots = [];
    if (scope && scope.nodeType === 1 && scope.hasAttribute('data-pdp-reveal')) roots.push(scope);
    Array.prototype.push.apply(roots, (scope || doc).querySelectorAll('[data-pdp-reveal]'));
    if (!roots.length) return;

    if (inDesignMode() || PDP.reducedMotion || !('IntersectionObserver' in win)) {
      roots.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    if (!revealObserver) {
      /* a rootMargin cannot change later: the sticky bar's height is read once, here */
      var bar = doc.querySelector('pdp-sticky-bar');
      var margin = 50 + ((bar && bar.offsetHeight) || 0);
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) reveal(entry.target);
        });
      }, { rootMargin: '0px 0px -' + margin + 'px 0px', threshold: 0 });
    }
    roots.forEach(function (el) {
      if (!el.classList.contains('is-revealed')) revealObserver.observe(el);
    });
  }

  function revealEverything() {
    Array.prototype.forEach.call(doc.querySelectorAll('[data-pdp-reveal]'), function (el) { el.classList.add('is-revealed'); });
  }

  whenParsed(function () {
    try { watchReveal(doc); } catch (e) { revealEverything(); }
  });
  doc.addEventListener('shopify:section:load', function (event) {
    try { watchReveal(event.target); } catch (e) { revealEverything(); }
  });
  /* content must never stay hidden because a tab was restored from the back/forward cache half-way */
  win.addEventListener('pageshow', function (event) { if (event.persisted) revealEverything(); });

  if (!('customElements' in win)) return;

  function define(name, ctor) {
    if (!win.customElements.get(name)) win.customElements.define(name, ctor);
  }

  /* ------------------------------------------------------------------ <pdp-carousel> */

  var SPEED = 400;

  class PdpCarousel extends HTMLElement {
    connectedCallback() {
      var self = this;
      this._alive = true;
      whenParsed(function () { if (self._alive) self._mount(); });
    }

    disconnectedCallback() {
      this._alive = false;
      this._destroy();
    }

    /* ---- public API ---- */

    go(index, instant) {
      if (!this._mounted || this._static) return;
      var i = parseInt(index, 10);
      if (!isFinite(i)) return;
      if (this._loop) {
        if (this._moving && !instant) return; /* like Splide: one move at a time keeps the clone jump invisible */
        i = clamp(i, -this._cc, this._n - 1 + this._cc);
      } else {
        i = clamp(i, 0, this._count - 1);
      }
      this._moveTo(i, !!instant);
    }

    next() {
      if (!this._mounted || this._static) return;
      if (this._loop) this.go(this._pos + 1);
      else this.go(Math.min(this._index + 1, this._count - 1));
    }

    prev() {
      if (!this._mounted || this._static) return;
      if (this._loop) this.go(this._pos - 1);
      else this.go(Math.max(this._index - 1, 0));
    }

    refresh() {
      if (!this._mounted) { if (this._alive) this._mount(); return; }
      this._build();
    }

    get index() { return this._index || 0; }
    get count() { return this._count || 0; }

    /* ---- set-up ---- */

    _mount() {
      if (this._mounted) return;
      var track = this.querySelector('.pdp-carousel__track');
      var list = track && track.querySelector('.pdp-carousel__list');
      if (!track || !list) return;

      var self = this;
      this._track = track;
      this._list = list;
      this._nav = this.querySelector('.pdp-carousel__nav');
      this._dotsEl = this.querySelector('.pdp-carousel__dots');
      this._prevBtn = this.querySelector('.pdp-carousel__arrow--prev');
      this._nextBtn = this.querySelector('.pdp-carousel__arrow--next');
      this._index = 0;
      this._pos = 0;
      this._pauses = {};
      this._stopped = false;
      this._mounted = true;
      this._offs = [];

      if (!this.hasAttribute('role')) this.setAttribute('role', 'region');
      this.setAttribute('aria-roledescription', 'carousel');

      function on(target, type, fn, opts) {
        if (!target) return;
        target.addEventListener(type, fn, opts);
        self._offs.push(function () { target.removeEventListener(type, fn, opts); });
      }

      on(this._prevBtn, 'click', function () { self._userMove(); self.prev(); });
      on(this._nextBtn, 'click', function () { self._userMove(); self.next(); });
      on(this._dotsEl, 'click', function (e) {
        var dot = e.target.closest ? e.target.closest('.pdp-carousel__dot') : null;
        if (!dot) return;
        self._userMove();
        self.go(parseInt(dot.getAttribute('data-index'), 10));
      });
      on(this._dotsEl, 'keydown', function (e) {
        var step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!step || !self._dots || !self._dots.length) return;
        e.preventDefault();
        var nextDot = self._dots[(self._index + step + self._dots.length) % self._dots.length];
        self._userMove();
        self.go(parseInt(nextDot.getAttribute('data-index'), 10));
        nextDot.focus();
      });

      on(list, 'transitionend', function (e) {
        if (e.target === list && e.propertyName === 'transform') self._settle();
      });
      /* focus moving into a slide can scroll an overflow:hidden box: put it back */
      on(track, 'scroll', function () {
        if (track.scrollLeft) track.scrollLeft = 0;
        if (track.scrollTop) track.scrollTop = 0;
      }, { passive: true });

      /* drag (mouse + touch); vertical scrolling stays the browser's (touch-action: pan-y) */
      on(track, 'pointerdown', function (e) { self._dragStart(e); });
      on(track, 'pointermove', function (e) { self._dragMove(e); });
      on(track, 'pointerup', function (e) { self._dragEnd(e, false); });
      on(track, 'pointercancel', function (e) { self._dragEnd(e, true); });
      on(track, 'dragstart', function (e) { e.preventDefault(); });
      on(this, 'click', function (e) {
        if (self._draggedAt && (win.performance.now() - self._draggedAt) < 400) {
          e.preventDefault();
          e.stopPropagation();
        }
        self._draggedAt = 0;
      }, true);

      /* autoplay pauses */
      on(this, 'pointerenter', function (e) { if (e.pointerType === 'mouse') self._pause('hover', true); });
      on(this, 'pointerleave', function (e) { if (e.pointerType === 'mouse') self._pause('hover', false); });
      on(this, 'focusin', function () { self._pause('focus', true); });
      on(this, 'focusout', function (e) { if (!self.contains(e.relatedTarget)) self._pause('focus', false); });
      on(doc, 'visibilitychange', function () { self._pause('hidden', doc.hidden); });

      /* theme editor: selecting a slide's block shows that slide and stops autoplay */
      on(this, 'shopify:block:select', function (e) {
        var slide = e.target && e.target.closest ? e.target.closest('.pdp-carousel__slide') : null;
        if (!slide || !self._slides) return;
        var i = self._slides.indexOf(slide);
        if (i < 0) return;
        self._stopped = true;
        self._schedule();
        self.go(self._loop ? i : Math.min(i, self._count - 1), !e.detail || e.detail.load === true);
      });

      var onWidth = debounce(function () { self._updateHeight(); }, 200);
      var lastWidth = this.clientWidth;
      if ('ResizeObserver' in win) {
        this._ro = new ResizeObserver(function () {
          var w = self.clientWidth;
          if (w === lastWidth) { self._updateHeight(); return; }
          lastWidth = w;
          self._resized();
          onWidth();
        });
        this._ro.observe(this);
      } else {
        on(win, 'resize', debounce(function () { self._resized(); self._updateHeight(); }, 200), { passive: true });
      }
      var onBreakpoint = function () { self._resized(); };
      onMedia(mqDesktop, onBreakpoint);
      this._offs.push(function () { offMedia(mqDesktop, onBreakpoint); });

      if ('IntersectionObserver' in win) {
        this._io = new IntersectionObserver(function (entries) {
          self._pause('offscreen', !entries[entries.length - 1].isIntersecting);
        });
        this._io.observe(this);
      }

      this._track.scrollLeft = 0;
      this.classList.add('is-ready');
      this._build();
    }

    _destroy() {
      if (!this._mounted) return;
      this._mounted = false;
      clearTimeout(this._timer);
      clearTimeout(this._settleTimer);
      (this._offs || []).forEach(function (off) { try { off(); } catch (e) { /* ignore */ } });
      this._offs = [];
      if (this._ro) this._ro.disconnect();
      if (this._io) this._io.disconnect();
      this._ro = this._io = null;
      this._removeClones();
      (this._slides || []).forEach(function (slide) {
        slide.removeAttribute('inert');
        slide.removeAttribute('aria-hidden');
      });
      if (this._dotsEl) this._dotsEl.textContent = '';
      if (this._list) this._list.style.transform = '';
      this.style.removeProperty('--pdp-active-h');
      this.classList.remove('is-ready', 'is-static', 'is-dragging', 'is-jumping');
      this._drag = null;
    }

    _removeClones() {
      if (!this._list) return;
      Array.prototype.slice.call(this._list.querySelectorAll('[data-pdp-clone]')).forEach(function (clone) {
        if (clone.parentNode) clone.parentNode.removeChild(clone);
      });
    }

    /* slides per page: measured (so a calc() in --pp still works), else read off the custom property */
    _readPerPage() {
      var declared = Math.max(1, num(win.getComputedStyle(this).getPropertyValue('--_pp'), 1));
      try {
        var first = this._list.querySelector('.pdp-carousel__slide:not([data-pdp-clone])');
        var pad = this._padding();
        var view = this._track.clientWidth - pad.left - pad.right;
        var gap = num(win.getComputedStyle(this._list).columnGap, 0);
        var w = first ? first.getBoundingClientRect().width : 0;
        if (view > 0 && w > 0) {
          var measured = Math.round(((view + gap) / (w + gap)) * 20) / 20;
          if (measured >= 1 && Math.abs(measured - declared) > 0.04) return measured;
        }
      } catch (e) { /* fall through */ }
      return declared;
    }

    /* (re)builds clones, dots and ARIA for the current breakpoint */
    _build() {
      if (!this._mounted) return;
      var self = this;
      var list = this._list;

      clearTimeout(this._settleTimer);
      this._moving = false;
      this._removeClones();

      var slides = Array.prototype.filter.call(list.children, function (child) {
        return child.classList && child.classList.contains('pdp-carousel__slide');
      });
      this._slides = slides;
      var n = this._n = slides.length;
      var pp = this._pp = this._readPerPage();
      var whole = this._whole = Math.max(1, Math.floor(pp + 0.001));

      this._static = n <= whole;
      this._loop = this.hasAttribute('loop') && !this._static;
      this._cc = 0;
      this.classList.toggle('is-static', this._static);

      slides.forEach(function (slide, i) {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', (i + 1) + ' of ' + n);
      });

      if (this._loop) {
        var cc = this._cc = Math.ceil(pp) + 1;
        var before = doc.createDocumentFragment();
        var after = doc.createDocumentFragment();
        for (var k = 0; k < cc; k++) {
          after.appendChild(this._clone(slides[k % n]));
          before.appendChild(this._clone(slides[(((n - cc + k) % n) + n) % n]));
        }
        list.insertBefore(before, slides[0]);
        list.appendChild(after);
      }

      this._items = Array.prototype.filter.call(list.children, function (child) {
        return child.classList && child.classList.contains('pdp-carousel__slide');
      });
      this._count = this._static ? 1 : (this._loop ? n : Math.max(1, n - whole + 1));
      this._index = this._static ? 0 : clamp(this._index || 0, 0, this._count - 1);
      this._pos = this._index;

      /* dots: one per position (one per slide when looping) */
      this._dots = [];
      if (this._dotsEl) {
        this._dotsEl.textContent = '';
        if (!this._static) {
          for (var d = 0; d < this._count; d++) {
            var li = doc.createElement('li');
            var btn = doc.createElement('button');
            btn.type = 'button';
            btn.className = 'pdp-carousel__dot';
            btn.setAttribute('data-index', d);
            btn.setAttribute('aria-label', 'Go to slide ' + (d + 1));
            li.appendChild(btn);
            this._dotsEl.appendChild(li);
            this._dots.push(btn);
          }
        }
      }

      if (this._static) {
        list.style.transform = '';
        slides.forEach(function (slide) { slide.removeAttribute('inert'); slide.removeAttribute('aria-hidden'); });
        this.style.removeProperty('--pdp-active-h');
        this._schedule();
        return;
      }

      this._jump(this._pos);
      this._sync();
      this._updateHeight();
      this._schedule();

      /* slides whose height changes late (pictures, fonts) keep the adaptive height right */
      if (this._ro && this.hasAttribute('adaptive-height')) {
        slides.forEach(function (slide) { try { self._ro.observe(slide); } catch (e) { /* ignore */ } });
      }
    }

    _clone(slide) {
      var clone = slide.cloneNode(true);
      clone.setAttribute('data-pdp-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.removeAttribute('id');
      clone.removeAttribute('role');
      clone.removeAttribute('aria-label');
      clone.removeAttribute('aria-roledescription');
      clone.removeAttribute('data-shopify-editor-block');
      Array.prototype.forEach.call(clone.querySelectorAll('[id], [data-shopify-editor-block]'), function (el) {
        el.removeAttribute('id');
        el.removeAttribute('data-shopify-editor-block');
      });
      return clone;
    }

    /* ---- geometry ---- */

    _padding() {
      var cs = win.getComputedStyle(this._track);
      return { left: num(cs.paddingLeft), right: num(cs.paddingRight) };
    }

    /* left edge of the item that shows first at position p, measured from the list's own origin */
    _rawX(p) {
      var item = this._items[p + this._cc];
      if (!item) return 0;
      return item.getBoundingClientRect().left - this._list.getBoundingClientRect().left;
    }

    _maxX() {
      var items = this._items;
      if (!items.length) return 0;
      var pad = this._padding();
      var listLeft = this._list.getBoundingClientRect().left;
      var total = items[items.length - 1].getBoundingClientRect().right - listLeft;
      var view = this._track.clientWidth - pad.left - pad.right + (this.hasAttribute('peek-shift') ? pad.right : 0);
      return Math.max(0, total - view);
    }

    _x(p) {
      var x = this._rawX(p);
      if (this._loop) return x;
      if (this.hasAttribute('peek-shift')) {
        var peek = this._padding().right;
        if (p > 0) x -= (p >= this._count - 1 ? peek : peek / 2);
      }
      return clamp(x, 0, this._maxX());
    }

    _currentX() {
      var t = win.getComputedStyle(this._list).transform;
      if (!t || t === 'none') return 0;
      try {
        if ('DOMMatrixReadOnly' in win) return -new win.DOMMatrixReadOnly(t).m41;
      } catch (e) { /* fall through */ }
      var m = t.match(/matrix(3d)?\(([^)]+)\)/);
      if (!m) return 0;
      var parts = m[2].split(',');
      return -num(parts[m[1] ? 12 : 4]);
    }

    _setX(x) {
      this._list.style.transform = 'translate3d(' + (-x) + 'px,0,0)';
    }

    /* move with no transition, then let transitions work again */
    _jump(p) {
      this.classList.add('is-jumping');
      this._setX(this._x(p));
      void this._list.offsetWidth;
      this.classList.remove('is-jumping');
    }

    _visible(p) {
      var out = [];
      for (var k = 0; k < this._whole; k++) {
        out.push(this._loop ? (((p + k) % this._n) + this._n) % this._n : p + k);
      }
      return out;
    }

    /* ---- moving ---- */

    _moveTo(p, instant) {
      var n = this._n;
      var index = this._loop ? ((p % n) + n) % n : p;
      var before = this._visible(this._index);
      var changed = index !== this._index;

      this._pos = p;
      this._index = index;
      this._updateHeight();            /* at the START of the move, so the height animates with the slide */
      this._sync();

      var after = this._visible(index);
      var slides = this._slides;
      before.forEach(function (i) {
        if (after.indexOf(i) < 0 && slides[i]) fire(slides[i], 'pdp:slide:hidden', { index: i });
      });

      clearTimeout(this._settleTimer);
      if (instant || PDP.reducedMotion) {
        this._jump(p);
        this._moving = false;
        this._settle(true);
      } else {
        var self = this;
        this._moving = true;
        this._setX(this._x(p));
        this._settleTimer = setTimeout(function () { self._settle(); }, SPEED + 100);
      }
      if (changed) fire(this, 'pdp:carousel:change', { index: index, count: this._count });
    }

    /* after a move: if it ended on a clone, swap to the real slide without anyone seeing it */
    _settle(quiet) {
      clearTimeout(this._settleTimer);
      if (!this._mounted) return;
      this._moving = false;
      if (this._loop && this._pos !== this._index) {
        this._pos = this._index;
        this._jump(this._pos);
      }
      if (!quiet) this._schedule();
    }

    _sync() {
      var index = this._index;
      (this._dots || []).forEach(function (dot, i) {
        var active = i === index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this._prevBtn) this._prevBtn.disabled = !this._loop && index <= 0;
      if (this._nextBtn) this._nextBtn.disabled = !this._loop && index >= this._count - 1;

      /* slides out of view are taken out of the tab order and the accessibility tree
         (not in the theme editor, where a click on any slide has to select its block) */
      var visible = this._visible(index);
      var editing = inDesignMode();
      this._slides.forEach(function (slide, i) {
        var show = visible.indexOf(i) >= 0;
        slide.classList.toggle('is-active', i === index);
        slide.classList.toggle('is-visible', show);
        if (editing) return;
        if (show) { slide.removeAttribute('inert'); slide.removeAttribute('aria-hidden'); }
        else { slide.setAttribute('inert', ''); slide.setAttribute('aria-hidden', 'true'); }
      });
    }

    _updateHeight() {
      if (!this._mounted || !this.hasAttribute('adaptive-height')) return;
      if (mqDesktop.matches || this._static) { this.style.removeProperty('--pdp-active-h'); return; }
      var slide = this._slides[this._index];
      if (!slide) return;
      var h = slide.offsetHeight;
      if (h > 0) this.style.setProperty('--pdp-active-h', h + 'px');
    }

    _resized() {
      if (!this._mounted) return;
      var pp = this._readPerPage();
      if (pp !== this._pp) { this._build(); return; }
      if (this._static) return;
      clearTimeout(this._settleTimer);
      this._moving = false;
      if (this._loop) this._pos = this._index;
      this._jump(this._pos);
    }

    /* ---- drag ---- */

    _dragStart(e) {
      if (this._static || !this._mounted) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.isPrimary === false) return;
      if (e.target && e.target.closest && e.target.closest('input, select, textarea, [contenteditable], [data-pdp-no-drag]')) return;
      this._drag = {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        base: 0,
        active: false,
        lastX: e.clientX,
        lastT: win.performance.now(),
        v: 0
      };
    }

    _dragMove(e) {
      var d = this._drag;
      if (!d || e.pointerId !== d.id) return;
      var dx = e.clientX - d.x;
      var dy = e.clientY - d.y;

      if (!d.active) {
        if (Math.abs(dx) > 5 && Math.abs(dx) > Math.abs(dy)) {
          d.active = true;
          d.x = e.clientX;                 /* start from here so the list does not jump by the threshold */
          dx = 0;
          /* take over from wherever the list is right now, even half-way through a move */
          d.base = this._currentX();
          clearTimeout(this._settleTimer);
          this._moving = false;
          if (this._loop) this._pos = this._index;
          this.classList.add('is-dragging');
          this._setX(d.base);
          this._pause('drag', true);
          try { this._track.setPointerCapture(d.id); } catch (err) { /* ignore */ }
          try { var sel = win.getSelection && win.getSelection(); if (sel && sel.removeAllRanges) sel.removeAllRanges(); } catch (err2) { /* ignore */ }
          d.x0 = this._rawX(0);
          d.step = this._n > 1 ? this._rawX(1) - d.x0 : 0;
          d.max = this._loop ? 0 : this._maxX();
        } else {
          if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) this._drag = null;
          return;
        }
      }

      if (e.cancelable) e.preventDefault();
      var now = win.performance.now();
      var dt = now - d.lastT;
      if (dt > 0) d.v = (e.clientX - d.lastX) / dt;
      d.lastX = e.clientX;
      d.lastT = now;

      var x = d.base - dx;
      if (this._loop && d.step > 0) {
        var span = this._n * d.step;
        while (x < d.x0) { x += span; d.base += span; }
        while (x >= d.x0 + span) { x -= span; d.base -= span; }
      } else {
        if (x < 0) x = x * 0.3;                                   /* rubber band at both ends */
        else if (x > d.max) x = d.max + (x - d.max) * 0.3;
      }
      d.current = x;
      this._setX(x);
    }

    _dragEnd(e, cancelled) {
      var d = this._drag;
      if (!d || (e && e.pointerId !== d.id)) return;
      this._drag = null;
      if (!d.active) return;

      try { this._track.releasePointerCapture(d.id); } catch (err) { /* ignore */ }
      this._draggedAt = win.performance.now();
      var x = d.current == null ? d.base : d.current;
      var target = this._index;

      if (d.step > 0) {
        var f = (x - d.x0) / d.step;
        var flick = !cancelled && Math.abs(d.v) > 0.35 && (win.performance.now() - d.lastT) < 120;
        if (flick) target = d.v < 0 ? Math.ceil(f - 0.05) : Math.floor(f + 0.05);
        else target = Math.round(f);
      }

      /* the transition starts from where the finger left the list */
      this.classList.remove('is-dragging');
      this._pause('drag', false);

      if (this._loop) target = clamp(target, -this._cc, this._n - 1 + this._cc);
      else target = clamp(target, 0, this._count - 1);
      this._moveTo(target, false);
    }

    /* ---- autoplay ---- */

    _userMove() {
      /* an explicit move restarts the autoplay clock */
      this._schedule();
    }

    _pause(reason, on) {
      if (!this._pauses) return;
      if (on) this._pauses[reason] = true;
      else delete this._pauses[reason];
      this._schedule();
    }

    _schedule() {
      clearTimeout(this._timer);
      if (!this._mounted || this._static || this._stopped || PDP.reducedMotion) return;
      var ms = num(this.getAttribute('autoplay'), 0);
      if (ms <= 0) return;
      for (var key in this._pauses) { if (this._pauses[key]) return; }
      var self = this;
      this._timer = setTimeout(function () {
        if (!self._mounted) return;
        if (!self._loop && self._index >= self._count - 1) self.go(0);
        else self.next();
        /* the next tick is scheduled by _settle(), or here when nothing moved */
        if (!self._moving) self._schedule();
      }, Math.max(ms, 1000));
    }
  }

  define('pdp-carousel', PdpCarousel);

  /* ------------------------------------------------------------------ <pdp-marquee> */

  class PdpMarquee extends HTMLElement {
    connectedCallback() {
      var self = this;
      if (!('IntersectionObserver' in win) || this._io) return;
      /* CSS does the work; this only stops the animation while nobody can see it */
      this._io = new IntersectionObserver(function (entries) {
        self.classList.toggle('is-paused', !entries[entries.length - 1].isIntersecting);
      }, { rootMargin: '100px 0px 100px 0px' });
      this._io.observe(this);
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      this._io = null;
      this.classList.remove('is-paused');
    }
  }

  define('pdp-marquee', PdpMarquee);

  /* ------------------------------------------------------------------ <pdp-tabs> */

  class PdpTabs extends HTMLElement {
    connectedCallback() {
      var self = this;
      this._alive = true;
      whenParsed(function () { if (self._alive) self._mount(); });
    }

    disconnectedCallback() {
      this._alive = false;
      (this._offs || []).forEach(function (off) { try { off(); } catch (e) { /* ignore */ } });
      this._offs = [];
      if (this._ro) this._ro.disconnect();
      this._ro = null;
      this._mounted = false;
    }

    _mount() {
      if (this._mounted) return;
      var self = this;
      this._tabs = Array.prototype.slice.call(this.querySelectorAll('.pdp-tabs__tab'));
      this._panels = Array.prototype.slice.call(this.querySelectorAll('.pdp-tabs__panel'));
      this._box = this.querySelector('.pdp-tabs__panels');
      if (!this._tabs.length || !this._panels.length) return;
      this._mounted = true;
      this._offs = [];

      function on(target, type, fn, opts) {
        if (!target) return;
        target.addEventListener(type, fn, opts);
        self._offs.push(function () { target.removeEventListener(type, fn, opts); });
      }

      var start = 0;
      this._tabs.forEach(function (tab, i) { if (tab.classList.contains('is-active')) start = i; });
      this._index = -1;
      this.select(start, { silent: true, instant: true });

      this._tabs.forEach(function (tab, i) {
        on(tab, 'click', function () { self.select(i); });
      });
      on(this, 'keydown', function (e) {
        var tab = e.target && e.target.closest ? e.target.closest('.pdp-tabs__tab') : null;
        if (!tab) return;
        var i = self._tabs.indexOf(tab);
        var last = self._tabs.length - 1;
        var to = -1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') to = i >= last ? 0 : i + 1;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') to = i <= 0 ? last : i - 1;
        else if (e.key === 'Home') to = 0;
        else if (e.key === 'End') to = last;
        if (to < 0) return;
        e.preventDefault();
        self.select(to);
        self._tabs[to].focus();
      });
      on(this, 'shopify:block:select', function (e) {
        var hit = e.target && e.target.closest ? e.target.closest('.pdp-tabs__tab, .pdp-tabs__panel') : null;
        if (!hit) return;
        var i = hit.classList.contains('pdp-tabs__tab') ? self._tabs.indexOf(hit) : self._panels.indexOf(hit);
        if (i >= 0) self.select(i);
      });

      var fit = function () { self._fit(); };
      on(win, 'load', fit);
      on(win, 'resize', debounce(fit, 100), { passive: true });
      if (doc.fonts && doc.fonts.ready && doc.fonts.ready.then) doc.fonts.ready.then(fit, noop);
      if ('ResizeObserver' in win) {
        this._ro = new ResizeObserver(fit);
        this._panels.forEach(function (panel) { self._ro.observe(panel); });
      }
    }

    select(index, opts) {
      if (!this._mounted) return;
      var i = clamp(parseInt(index, 10) || 0, 0, this._tabs.length - 1);
      var options = opts || {};
      var changed = i !== this._index;
      this._index = i;

      this._tabs.forEach(function (tab, t) {
        var active = t === i;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });
      this._panels.forEach(function (panel, p) {
        var active = p === i;
        panel.classList.toggle('is-active', active);
        if (active) { panel.removeAttribute('hidden'); panel.removeAttribute('aria-hidden'); }
        else panel.setAttribute('aria-hidden', 'true');
      });

      if (options.instant && this._box) {
        var box = this._box;
        var previous = box.style.transition;
        box.style.transition = 'none';
        this._fit();
        void box.offsetHeight;
        box.style.transition = previous;
      } else {
        this._fit();
      }
      if (changed && !options.silent) fire(this, 'pdp:tabs:change', { index: i });
    }

    _fit() {
      if (!this._box || !this._panels) return;
      var panel = this._panels[this._index];
      if (!panel) return;
      var h = panel.offsetHeight;
      if (h > 0) this._box.style.height = h + 'px';
    }
  }

  define('pdp-tabs', PdpTabs);

  /* ------------------------------------------------------------------ <pdp-accordion> */

  class PdpAccordion extends HTMLElement {
    connectedCallback() {
      var self = this;
      this._alive = true;
      whenParsed(function () { if (self._alive) self._mount(); });
    }

    disconnectedCallback() {
      this._alive = false;
      (this._offs || []).forEach(function (off) { try { off(); } catch (e) { /* ignore */ } });
      this._offs = [];
      this._mounted = false;
    }

    _mount() {
      if (this._mounted) return;
      var self = this;
      var details = this._details = this.querySelector('details');
      if (!details) return;
      this._summary = details.querySelector('summary');
      this._panel = this.querySelector('.pdp-accordion__panel');
      this._mounted = true;
      this._offs = [];

      var sync = function () { self._sync(); };
      details.addEventListener('toggle', sync);             /* toggle does not bubble */
      this._offs.push(function () { details.removeEventListener('toggle', sync); });

      var select = function () { if (!details.open) { details.open = true; self._sync(); } };
      this.addEventListener('shopify:block:select', select);
      this._offs.push(function () { self.removeEventListener('shopify:block:select', select); });

      this._sync();
    }

    _sync() {
      var open = !!(this._details && this._details.open);
      if (this._summary) this._summary.setAttribute('aria-expanded', open ? 'true' : 'false');
      /* a closed answer is only collapsed visually: keep it out of the tab order and the accessibility tree */
      setInert(this._panel, !open);
      this.classList.toggle('is-open', open);
    }

    get open() { return !!(this._details && this._details.open); }
    set open(value) {
      if (!this._details) return;
      this._details.open = !!value;
      this._sync();
    }
  }

  define('pdp-accordion', PdpAccordion);

  /* ------------------------------------------------------------------ <pdp-video> */

  class PdpVideo extends HTMLElement {
    connectedCallback() {
      var self = this;
      this._alive = true;
      whenParsed(function () { if (self._alive) self._mount(); });
    }

    disconnectedCallback() {
      this._alive = false;
      if (this._video) { try { this._video.pause(); } catch (e) { /* ignore */ } }
      if (this._raf) win.cancelAnimationFrame(this._raf);
      (this._offs || []).forEach(function (off) { try { off(); } catch (e) { /* ignore */ } });
      this._offs = [];
      if (this._io) this._io.disconnect();
      this._io = null;
      this._mounted = false;
    }

    play() {
      var self = this;
      var video = this._video;
      if (!video) return;
      if (this.hasAttribute('pause-others')) {
        Array.prototype.forEach.call(doc.querySelectorAll('pdp-video'), function (other) {
          if (other !== self && typeof other.pause === 'function') other.pause();
        });
      }
      this.classList.add('is-playing');
      var result;
      try { result = video.play(); } catch (e) { this.classList.remove('is-playing'); return; }
      if (result && result.catch) result.catch(function () { self.classList.remove('is-playing'); });
    }

    pause() {
      if (this._video && !this._video.paused) this._video.pause();
    }

    toggle() {
      if (!this._video) return;
      if (this._video.paused || this._video.ended) this.play();
      else this.pause();
    }

    _mount() {
      if (this._mounted) return;
      var self = this;
      var video = this._video = this.querySelector('video');
      if (!video) return;
      this._mounted = true;
      this._offs = [];
      this._playBtn = this.querySelector('.pdp-video__play');
      this._soundBtn = this.querySelector('.pdp-video__sound');
      this._timeline = this.querySelector('.pdp-video__timeline');
      this._progress = this.querySelector('.pdp-video__progress');

      function on(target, type, fn, opts) {
        if (!target) return;
        target.addEventListener(type, fn, opts);
        self._offs.push(function () { target.removeEventListener(type, fn, opts); });
      }

      /* a tap anywhere on the player toggles it, except on the sound button and the timeline */
      on(this, 'click', function (e) {
        var t = e.target;
        if (t && t.closest && (t.closest('.pdp-video__sound') || t.closest('.pdp-video__timeline'))) return;
        if (t && t.closest && t.closest('a[href]')) return;
        self.toggle();
      });

      on(this._soundBtn, 'click', function (e) {
        e.stopPropagation();
        video.muted = !video.muted;
        self._syncSound();
      });
      on(video, 'volumechange', function () { self._syncSound(); });

      on(video, 'play', function () {
        self.classList.add('is-playing');
        if (self._playBtn) self._playBtn.setAttribute('aria-label', 'Pause video');
        self._tick();
        fire(self, 'pdp:video:play', {});
      });
      on(video, 'pause', function () {
        self.classList.remove('is-playing');
        if (self._playBtn) self._playBtn.setAttribute('aria-label', 'Play video');
        self._paint();
      });
      on(video, 'ended', function () {
        /* back to the poster */
        self.classList.remove('is-playing', 'is-loading');
        try { video.currentTime = 0; video.load(); } catch (e) { /* ignore */ }
        self._paint(0);
      });
      on(video, 'waiting', function () { if (!video.paused) self.classList.add('is-loading'); });
      on(video, 'canplay', function () { self.classList.remove('is-loading'); });
      on(video, 'playing', function () { self.classList.remove('is-loading'); });
      on(video, 'timeupdate', function () { if (video.paused) self._paint(); });
      on(video, 'error', function () { self.classList.remove('is-playing', 'is-loading'); });

      /* scrubbing */
      var scrubbing = false;
      function seek(e) {
        var rect = self._timeline.getBoundingClientRect();
        if (!rect.width || !isFinite(video.duration) || !video.duration) return;
        var ratio = clamp((e.clientX - rect.left) / rect.width, 0, 1);
        try { video.currentTime = ratio * video.duration; } catch (err) { /* ignore */ }
        self._paint(ratio);
      }
      on(this._timeline, 'pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        scrubbing = true;
        e.stopPropagation();
        if (e.cancelable) e.preventDefault();
        try { self._timeline.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
        seek(e);
      });
      on(this._timeline, 'pointermove', function (e) { if (scrubbing) seek(e); });
      var stop = function (e) {
        if (!scrubbing) return;
        scrubbing = false;
        try { self._timeline.releasePointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      };
      on(this._timeline, 'pointerup', stop);
      on(this._timeline, 'pointercancel', stop);
      on(this._timeline, 'click', function (e) { e.stopPropagation(); });

      /* a slide that leaves the carousel's window, or a player scrolled fully away, stops talking */
      var slide = this.closest('.pdp-carousel__slide');
      on(slide, 'pdp:slide:hidden', function () { self.pause(); });
      if ('IntersectionObserver' in win) {
        this._io = new IntersectionObserver(function (entries) {
          if (!entries[entries.length - 1].isIntersecting) self.pause();
        });
        this._io.observe(this);
      }
      on(doc, 'visibilitychange', function () { if (doc.hidden) self.pause(); });

      this._syncSound();
      this._paint(0);
    }

    _syncSound() {
      var muted = !!(this._video && this._video.muted);
      this.classList.toggle('is-muted', muted);
      if (this._soundBtn) {
        this._soundBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
        this._soundBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
      }
    }

    _paint(ratio) {
      var video = this._video;
      var r = ratio;
      if (r == null) r = video && video.duration ? video.currentTime / video.duration : 0;
      r = clamp(isFinite(r) ? r : 0, 0, 1);
      var pct = (r * 100).toFixed(2) + '%';
      if (this._progress) this._progress.style.width = pct;
      this.style.setProperty('--pdp-video-progress', pct);
    }

    _tick() {
      var self = this;
      if (this._raf) win.cancelAnimationFrame(this._raf);
      var loop = function () {
        self._paint();
        if (self._mounted && self._video && !self._video.paused && !self._video.ended) self._raf = win.requestAnimationFrame(loop);
        else self._raf = 0;
      };
      this._raf = win.requestAnimationFrame(loop);
    }
  }

  define('pdp-video', PdpVideo);

  /* ------------------------------------------------------------------ <pdp-hotspot> */

  var openHotspots = [];

  function closeHotspotsOutside(target) {
    openHotspots.slice().forEach(function (spot) {
      if (!target || !spot.contains(target)) spot.close();
    });
  }

  doc.addEventListener('pointerdown', function (e) { if (openHotspots.length) closeHotspotsOutside(e.target); }, true);
  doc.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    if (!openHotspots.length) return;
    openHotspots.slice().forEach(function (spot) {
      var hadFocus = spot.contains(doc.activeElement);
      spot.close();
      if (hadFocus && spot._dot) spot._dot.focus();
    });
  });

  class PdpHotspot extends HTMLElement {
    connectedCallback() {
      var self = this;
      this._alive = true;
      whenParsed(function () { if (self._alive) self._mount(); });
    }

    disconnectedCallback() {
      this._alive = false;
      clearTimeout(this._leaveTimer);
      clearTimeout(this._flipTimer);
      (this._offs || []).forEach(function (off) { try { off(); } catch (e) { /* ignore */ } });
      this._offs = [];
      var at = openHotspots.indexOf(this);
      if (at >= 0) openHotspots.splice(at, 1);
      this._mounted = false;
    }

    _mount() {
      if (this._mounted) return;
      var self = this;
      this._dot = this.querySelector('.pdp-hotspot__dot');
      this._card = this.querySelector('.pdp-hotspot__card');
      if (!this._dot || !this._card) return;
      this._mounted = true;
      this._offs = [];
      this._dot.setAttribute('aria-expanded', this.classList.contains('is-open') ? 'true' : 'false');
      setInert(this._card, !this.classList.contains('is-open'));

      function on(target, type, fn, opts) {
        target.addEventListener(type, fn, opts);
        self._offs.push(function () { target.removeEventListener(type, fn, opts); });
      }

      on(this._dot, 'click', function (e) {
        e.preventDefault();
        /* with a mouse the card is already open from hovering: a click then keeps it open instead of toggling it shut */
        if (self._hovered && self.isOpen) return;
        self.toggle();
      });

      /* data-hover="false" (a section's "Open on hover" switch, off): click, tap and keyboard only */
      on(this, 'pointerenter', function (e) {
        if (e.pointerType !== 'mouse' || !mqHover.matches || self.getAttribute('data-hover') === 'false') return;
        clearTimeout(self._leaveTimer);
        self._hovered = true;
        self.open();
      });
      on(this, 'pointerleave', function (e) {
        if (e.pointerType !== 'mouse' || !mqHover.matches || self.getAttribute('data-hover') === 'false') return;
        clearTimeout(self._leaveTimer);
        self._leaveTimer = setTimeout(function () {
          self._hovered = false;
          if (!self.contains(doc.activeElement)) self.close();
        }, 200);
      });

      on(this, 'focusout', function (e) {
        if (self.isOpen && !self.contains(e.relatedTarget) && !self._hovered) self.close();
      });
      on(this, 'shopify:block:select', function () { self.open(); });
      on(this, 'shopify:block:deselect', function () { self.close(); });
      on(win, 'resize', debounce(function () { if (self.isOpen) self._place(); }, 100), { passive: true });
    }

    get isOpen() { return this.classList.contains('is-open'); }

    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    }

    open() {
      if (!this._mounted || this.isOpen) return;
      var self = this;
      /* one open hotspot per section */
      var scope = this.closest('.pdp') || this.closest('.shopify-section') || doc;
      openHotspots.slice().forEach(function (spot) {
        if (spot !== self && scope.contains(spot)) spot.close();
      });
      clearTimeout(this._flipTimer);
      this.classList.add('is-open');
      this._dot.setAttribute('aria-expanded', 'true');
      setInert(this._card, false);
      if (openHotspots.indexOf(this) < 0) openHotspots.push(this);
      this._place();
      fire(this, 'pdp:hotspot:open', {});
    }

    close() {
      if (!this._mounted || !this.isOpen) return;
      var self = this;
      this.classList.remove('is-open');
      this._dot.setAttribute('aria-expanded', 'false');
      setInert(this._card, true);
      var at = openHotspots.indexOf(this);
      if (at >= 0) openHotspots.splice(at, 1);
      /* drop the flip only after the fade-out, so the card does not jump on its way out */
      clearTimeout(this._flipTimer);
      this._flipTimer = setTimeout(function () {
        if (!self.isOpen) self.classList.remove('is-flipped');
      }, 120);
      fire(this, 'pdp:hotspot:close', {});
    }

    /* opens upward; flips downward when the top would hide under the header or above the viewport; and is pushed
       sideways so it never leaves the viewport */
    _place() {
      var card = this._card;
      this.classList.remove('is-flipped');
      card.style.setProperty('--pdp-hotspot-shift', '0px');
      var rect = card.getBoundingClientRect();
      if (rect.top < headerOffset()) {
        this.classList.add('is-flipped');
        rect = card.getBoundingClientRect();
      }
      var edge = 14.25;
      var width = html.clientWidth || win.innerWidth;
      var shift = 0;
      if (rect.width + edge * 2 >= width) shift = (width - rect.width) / 2 - rect.left;
      else if (rect.left < edge) shift = edge - rect.left;
      else if (rect.right > width - edge) shift = width - edge - rect.right;
      card.style.setProperty('--pdp-hotspot-shift', Math.round(shift * 100) / 100 + 'px');
    }
  }

  define('pdp-hotspot', PdpHotspot);

  /* ------------------------------------------------------------------ <pdp-sticky-bar> */

  class PdpStickyBar extends HTMLElement {
    connectedCallback() {
      var self = this;
      this._alive = true;
      whenParsed(function () { if (self._alive) self._mount(); });
    }

    disconnectedCallback() {
      this._alive = false;
      (this._offs || []).forEach(function (off) { try { off(); } catch (e) { /* ignore */ } });
      this._offs = [];
      if (this._io) this._io.disconnect();
      if (this._mo) this._mo.disconnect();
      this._io = this._mo = null;
      if (this._raf) win.cancelAnimationFrame(this._raf);
      clearTimeout(this._loadingTimer);
      this._mounted = false;
      this._writeHeight(false);
    }

    _find(selector) {
      if (!selector) return null;
      try { return doc.querySelector(selector); } catch (e) { return null; }
    }

    _mount() {
      if (this._mounted) return;
      var self = this;
      this._mounted = true;
      this._offs = [];
      this._shown = false;
      this._button = this.querySelector('[data-pdp-sticky-btn]');
      this._watched = this._find(this.getAttribute('data-watch'));

      function on(target, type, fn, opts) {
        if (!target) return;
        target.addEventListener(type, fn, opts);
        self._offs.push(function () { target.removeEventListener(type, fn, opts); });
      }

      this._apply(false);
      on(this._button, 'click', function (e) { e.preventDefault(); self._act(); });

      /* no add-to-cart on the page (its block was removed): the bar never shows */
      if (!this._watched) return;

      var check = function () {
        if (self._raf) return;
        self._raf = win.requestAnimationFrame(function () { self._raf = 0; self._update(); });
      };
      on(win, 'scroll', check, { passive: true });
      on(win, 'resize', check, { passive: true });
      on(win, 'load', check);
      if ('IntersectionObserver' in win) {
        this._io = new IntersectionObserver(check);
        this._io.observe(this._watched);
      }

      if (this._mode() === 'add' && 'MutationObserver' in win) {
        this._mo = new MutationObserver(function () { self._mirror(); });
        this._mo.observe(this._watched, { attributes: true, attributeFilter: ['disabled', 'class', 'aria-disabled'], childList: true, subtree: true, characterData: true });
        on(doc, 'pdp:cart:added', function () { self._loading(false); });
        this._mirror();
      }
      this._update();
    }

    _mode() {
      return this.getAttribute('data-action') === 'add' ? 'add' : 'scroll';
    }

    _update() {
      if (!this._mounted) return;
      var watched = this._watched;
      if (!watched || !watched.isConnected) {
        watched = this._watched = this._find(this.getAttribute('data-watch'));
        if (!watched) { this._apply(false); return; }
      }
      var show;
      if (this.getAttribute('data-display') === 'always') show = true;
      else {
        var rect = watched.getBoundingClientRect();
        /* a hidden button measures 0 x 0 at the origin: that is not "scrolled past" */
        show = (rect.width > 0 || rect.height > 0) && rect.bottom < 0;
      }
      this._apply(show);
    }

    _apply(show) {
      var changed = show !== this._shown;
      this._shown = show;
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      setInert(this, !show);
      if (!show && changed && this.contains(doc.activeElement)) { try { doc.activeElement.blur(); } catch (e) { /* ignore */ } }
      this._writeHeight(show);
    }

    _writeHeight(show) {
      html.style.setProperty('--pdp-sticky-h', (show ? this.offsetHeight : 0) + 'px');
    }

    _labelEl() {
      var button = this._button;
      if (!button) return null;
      return button.querySelector('[data-pdp-sticky-label]') ||
        button.querySelector('.pdp-btn__label') ||
        button.querySelector('span:not(.pdp-btn__spinner):not(.pdp-visually-hidden)') ||
        (button.children.length ? null : button);
    }

    /* add mode: the bar's button says and does what the real one does */
    _mirror() {
      var button = this._button;
      var atc = this._watched;
      if (!button || !atc) return;
      var label = this._labelEl();
      /* the bar's own wording: data-label when the section provides it, else what the button says at first sight */
      if (label && this._ownLabel == null) this._ownLabel = label.getAttribute('data-label') || label.textContent;

      var disabled = !!atc.disabled || atc.getAttribute('aria-disabled') === 'true';
      button.disabled = disabled;
      if (label) {
        var source = atc.querySelector('[data-pdp-atc-label]');
        var text = disabled ? ((source ? source.textContent : atc.textContent) || '').trim() : this._ownLabel;
        if (text && label.textContent !== text) label.textContent = text;
      }
      if (!atc.classList.contains('is-loading')) this._loading(false);
    }

    _loading(on) {
      clearTimeout(this._loadingTimer);
      if (this._button) this._button.classList.toggle('is-loading', !!on);
      if (on) {
        var self = this;
        this._loadingTimer = setTimeout(function () { self._loading(false); }, 8000);
      }
    }

    _act() {
      if (this._mode() === 'scroll') {
        var target = this._find(this.getAttribute('data-scroll-to')) || this._find('#pdp-buy') || this._watched;
        if (target) scrollToEl(target);
        return;
      }
      var formId = this.getAttribute('data-form');
      var form = (formId && doc.getElementById(formId)) || (this._watched && this._watched.form) || null;
      if (!form) {
        if (this._watched) scrollToEl(this._find('#pdp-buy') || this._watched);
        return;
      }
      var submit = this._watched && this._watched.form === form && this._watched.type === 'submit'
        ? this._watched
        : form.querySelector('[type="submit"][name="add"], [type="submit"]');
      if (submit && submit.disabled) return;
      this._loading(true);
      try {
        /* click the real button rather than requestSubmit(): a bundle app (Kaching) acts on that
           button's click and sends the shopper straight to checkout; a bare submit skips it and
           falls through to the add-to-drawer in pdp-main.js */
        if (submit) submit.click();
        else if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      } catch (e) {
        this._loading(false);
      }
    }
  }

  define('pdp-sticky-bar', PdpStickyBar);
})();
